// Edge lifecycle (WP3, §10.1): add an edge, install command with the
// one-time enrollment token, VPN keys, disable/enable.
import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from 'react';
import type {
  CreatedEdge,
  Edge,
  EdgeVpn,
  EnrollmentToken,
  LifecycleResult,
  Site,
  VpnKey,
} from './api/types.js';
import { useApi } from './auth.js';
import { Age, CopyBlock } from './components.js';
import { formatTimestamp } from './lib/format.js';
import {
  enrollmentState,
  formatExpiry,
  idProblem,
  lifecycleSummary,
  vpnUpCommand,
} from './lib/enrollment.js';

const asError = (err: unknown) => (err instanceof Error ? err : new Error(String(err)));

/** What the site page shows above the edge table after an action. */
export type EdgePanel =
  | { kind: 'install'; edgeNodeId: string; token: EnrollmentToken; created?: boolean }
  | { kind: 'vpn'; edgeNodeId: string; key: VpnKey }
  | { kind: 'result'; action: 'disable' | 'enable'; edgeId: string; result: LifecycleResult };

const NEW_SITE = '__new__';

/** Creates an edge and right away its first enrollment token. */
export function AddEdgeForm({
  sites,
  siteId,
  onCreated,
  onCancel,
}: {
  sites: readonly Site[];
  /** Fixed site (site page); otherwise the operator picks one or a new one. */
  siteId?: string;
  onCreated: (edge: CreatedEdge, token: EnrollmentToken | null, tokenError: Error | null) => void;
  onCancel: () => void;
}) {
  const api = useApi();
  const [site, setSite] = useState(siteId ?? sites[0]?.id ?? NEW_SITE);
  const [groupId, setGroupId] = useState('');
  const [siteName, setSiteName] = useState('');
  const [nodeId, setNodeId] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [touched, setTouched] = useState(false);

  const isNew = site === NEW_SITE;
  const existing = sites.find((s) => s.id === site);
  const problems = [
    isNew ? idProblem(groupId.trim(), 'site group id') : null,
    isNew && !siteName.trim() ? 'Enter the site name.' : null,
    idProblem(nodeId.trim(), 'edge node id'),
  ].filter((p): p is string => p !== null);

  async function submit(e: SyntheticEvent<HTMLFormElement>) {
    e.preventDefault();
    setTouched(true);
    if (problems.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      const created = await api.createEdge({
        group_id: isNew ? groupId.trim() : (existing?.sparkplug_group_id ?? ''),
        edge_node_id: nodeId.trim(),
        ...(name.trim() && { name: name.trim() }),
        ...(isNew && { site_name: siteName.trim() }),
      });
      let token: EnrollmentToken | null = null;
      let tokenError: Error | null = null;
      try {
        token = await api.createEnrollmentToken(created.id);
      } catch (err) {
        tokenError = asError(err);
      }
      onCreated(created, token, tokenError);
    } catch (err) {
      setError(asError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="card edge-form"
      onSubmit={(e) => {
        void submit(e);
      }}
    >
      <div className="section-head">
        <h2>Add edge</h2>
      </div>
      {!siteId && (
        <label className="field">
          <span>Site</span>
          <select
            value={site}
            onChange={(e) => {
              setSite(e.target.value);
            }}
          >
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.sparkplug_group_id})
              </option>
            ))}
            <option value={NEW_SITE}>New site…</option>
          </select>
        </label>
      )}
      {isNew && (
        <div className="edge-form-row">
          <label className="field">
            <span>Site name</span>
            <input
              type="text"
              maxLength={200}
              placeholder="e.g. Elements Bratislava"
              value={siteName}
              onChange={(e) => {
                setSiteName(e.target.value);
              }}
            />
          </label>
          <label className="field">
            <span>Site group id</span>
            <input
              type="text"
              className="mono"
              maxLength={63}
              placeholder="e.g. elements"
              value={groupId}
              onChange={(e) => {
                setGroupId(e.target.value);
              }}
            />
          </label>
        </div>
      )}
      <div className="edge-form-row">
        <label className="field">
          <span>Edge node id</span>
          <input
            type="text"
            className="mono"
            maxLength={63}
            placeholder="e.g. elements-ru02"
            value={nodeId}
            onChange={(e) => {
              setNodeId(e.target.value);
            }}
          />
        </label>
        <label className="field">
          <span>
            Name <span className="muted small">(optional)</span>
          </span>
          <input
            type="text"
            maxLength={200}
            placeholder="e.g. Plant room VM"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>
      </div>
      <p className="muted small">
        The edge node id becomes the certificate name, the MQTT user and the VPN host name; it
        cannot be changed later and must be unique across all sites.
      </p>
      {touched &&
        problems.map((p) => (
          <p key={p} className="form-error">
            {p}
          </p>
        ))}
      {error && <p className="form-error">{error.message}</p>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Creating…' : 'Create edge'}
        </button>
        <button type="button" className="btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** The install command with the one-time token: shown once. */
export function InstallPanel({
  edgeNodeId,
  token,
  created,
  onClose,
}: {
  edgeNodeId: string;
  token: EnrollmentToken;
  created?: boolean | undefined;
  onClose: () => void;
}) {
  return (
    <div className="card token-created" role="status">
      <div className="section-head">
        <h2>
          {created ? 'Edge ' : 'Install command for '}
          <span className="mono">{edgeNodeId}</span>
          {created ? ' created' : ''}
        </h2>
        <button type="button" className="btn btn-small" onClick={onClose}>
          Done
        </button>
      </div>
      <p className="warn strong">
        Copy the command now. The token is shown only once and works once, until{' '}
        {formatTimestamp(token.expires_at)}. Lost it? Get a new install command.
      </p>
      <CopyBlock label="Run on the edge VM (Debian/Ubuntu, as root)" text={token.install_command} />
      <p className="muted small">
        The script installs Docker, enrolls the edge (its private key stays on the VM) and starts
        the agent. Running it again with a new token on an installed VM re-enrolls it.
      </p>
    </div>
  );
}

export function VpnKeyPanel({
  edgeNodeId,
  vpnKey,
  onClose,
}: {
  edgeNodeId: string;
  vpnKey: VpnKey;
  onClose: () => void;
}) {
  return (
    <div className="card token-created" role="status">
      <div className="section-head">
        <h2>
          VPN key for <span className="mono">{edgeNodeId}</span>
        </h2>
        <button type="button" className="btn btn-small" onClick={onClose}>
          Done
        </button>
      </div>
      <p className="warn strong">Single use, valid for about an hour; shown only now.</p>
      <CopyBlock label="Run on the edge VM" text={vpnUpCommand(vpnKey, edgeNodeId)} />
      <CopyBlock label="Key only" text={vpnKey.auth_key} />
    </div>
  );
}

export function LifecycleResultPanel({
  panel,
  edgeNodeId,
  onRetry,
  onClose,
}: {
  panel: Extract<EdgePanel, { kind: 'result' }>;
  edgeNodeId: string;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { result, action } = panel;
  const lines = lifecycleSummary(result);
  return (
    <div className={`card token-created${result.complete ? '' : ' card-alert'}`} role="status">
      <div className="section-head">
        <h2>
          <span className="mono">{edgeNodeId}</span> {action === 'disable' ? 'disabled' : 'enabled'}
          {result.complete ? '' : ' — some steps failed'}
        </h2>
        <div className="row-actions">
          {!result.complete && (
            <button type="button" className="btn btn-small" onClick={onRetry}>
              Retry
            </button>
          )}
          <button type="button" className="btn btn-small" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      <ul className="step-list">
        {result.steps.map((s, i) => (
          <li
            key={s.step}
            className={s.status === 'failed' ? 'bad' : s.status === 'skipped' ? 'muted' : undefined}
          >
            {lines[i]}
          </li>
        ))}
      </ul>
      {action === 'enable' && (
        <p className="muted small">
          The edge can connect to MQTT again with its certificate (re-enroll it if the certificate
          expired meanwhile). The VPN needs a new key: use <strong>VPN key</strong>.
        </p>
      )}
    </div>
  );
}

/** A modal confirmation with an explanation (native `<dialog>`). */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  danger,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  error?: Error | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => {
      d?.close();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby="confirm-title"
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onCancel();
      }}
    >
      <h2 id="confirm-title">{title}</h2>
      <div className="dialog-body">{children}</div>
      {error && <p className="form-error">{error.message}</p>}
      <div className="form-actions">
        <button
          type="button"
          className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
          disabled={busy}
          onClick={onConfirm}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </dialog>
  );
}

/** Enrollment badge plus certificate or token details. */
export function EnrollmentCell({
  edge,
  now,
  timeZone,
}: {
  edge: Edge;
  now: number;
  timeZone: string;
}) {
  const st = enrollmentState(edge, now);
  const expires = edge.cert_expires_at ?? null;
  return (
    <>
      <span className={`badge badge-${st.tone === 'warn' ? 'config-sent' : st.tone}`}>
        {st.label}
      </span>
      {st.kind === 'disabled' && edge.disabled_at != null && (
        <div className="muted small">since {formatTimestamp(edge.disabled_at, timeZone)}</div>
      )}
      {st.kind === 'token-issued' && edge.enrollment_token_expires_at != null && (
        <div className="muted small">
          token expires {formatExpiry(edge.enrollment_token_expires_at, now)}
        </div>
      )}
      {edge.cert_serial && (
        <div className="muted small" title={`Certificate serial ${edge.cert_serial}`}>
          cert <span className="mono">{edge.cert_serial.slice(0, 12)}</span>
          {expires !== null && (
            <span title={formatTimestamp(expires, timeZone)}>
              {' '}
              · {expires > now ? 'expires' : 'expired'} {formatExpiry(expires, now)}
            </span>
          )}
        </div>
      )}
    </>
  );
}

export function VpnCell({ vpn, now, timeZone }: { vpn: EdgeVpn; now: number; timeZone: string }) {
  if (!vpn.ip && !vpn.online && vpn.last_seen === null) {
    return <span className="badge badge-unknown">VPN: not joined</span>;
  }
  return (
    <>
      <span className={`badge badge-${vpn.online ? 'online' : 'offline'}`}>
        <span className="dot" aria-hidden="true" />
        VPN {vpn.online ? 'online' : 'offline'}
      </span>
      {vpn.ip && <div className="mono small">{vpn.ip}</div>}
      {!vpn.online && (
        <div className="small">
          <Age ts={vpn.last_seen} now={now} timeZone={timeZone} />
        </div>
      )}
    </>
  );
}

type Pending = 'disable' | 'enable' | null;

/** Install command / re-enroll, VPN key, disable / enable for one edge. */
export function EdgeLifecycleActions({
  edge,
  onPanel,
  onChanged,
}: {
  edge: Edge;
  onPanel: (p: EdgePanel) => void;
  onChanged: () => void;
}) {
  const api = useApi();
  const [confirm, setConfirm] = useState<Pending>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const disabled = edge.disabled_at != null;
  const enrolled = Boolean(edge.cert_serial);

  async function run(what: string, f: () => Promise<void>) {
    setBusy(what);
    setError(null);
    try {
      await f();
    } catch (err) {
      setError(asError(err));
    } finally {
      setBusy(null);
    }
  }

  const install = () =>
    run('install', async () => {
      const token = await api.createEnrollmentToken(edge.id);
      onPanel({ kind: 'install', edgeNodeId: edge.edge_node_id, token });
      onChanged();
    });
  const vpnKey = () =>
    run('vpn', async () => {
      const key = await api.createVpnKey(edge.id);
      onPanel({ kind: 'vpn', edgeNodeId: edge.edge_node_id, key });
    });
  const toggle = (action: 'disable' | 'enable') =>
    run(action, async () => {
      const result =
        action === 'disable' ? await api.disableEdge(edge.id) : await api.enableEdge(edge.id);
      setConfirm(null);
      onPanel({ kind: 'result', action, edgeId: edge.id, result });
      onChanged();
    });

  return (
    <>
      {!disabled && (
        <button
          type="button"
          className="btn btn-small"
          disabled={busy !== null}
          title={
            enrolled
              ? 'New one-time token: run the install command again on the VM (new key and certificate)'
              : 'One-time token and the command to run on the edge VM'
          }
          onClick={() => {
            void install();
          }}
        >
          {busy === 'install' ? '…' : enrolled ? 'Re-enroll' : 'Get install command'}
        </button>
      )}
      {!disabled && enrolled && edge.vpn != null && (
        <button
          type="button"
          className="btn btn-small"
          disabled={busy !== null}
          title="Single-use key to (re)join the management VPN"
          onClick={() => {
            void vpnKey();
          }}
        >
          {busy === 'vpn' ? '…' : 'VPN key'}
        </button>
      )}
      <button
        type="button"
        className={`btn btn-small${disabled ? '' : ' btn-danger-outline'}`}
        disabled={busy !== null}
        onClick={() => {
          setError(null);
          setConfirm(disabled ? 'enable' : 'disable');
        }}
      >
        {disabled ? 'Enable' : 'Disable'}
      </button>
      {error && confirm === null && (
        <span className="form-error small" role="alert">
          {error.message}
        </span>
      )}
      {confirm === 'disable' && (
        <ConfirmDialog
          title={`Disable ${edge.edge_node_id}?`}
          confirmLabel="Disable edge"
          danger
          busy={busy === 'disable'}
          error={error}
          onConfirm={() => {
            void toggle('disable');
          }}
          onCancel={() => {
            setConfirm(null);
          }}
        >
          <ul>
            <li>
              The MQTT broker bans the edge and closes its connection: no more data or commands.
            </li>
            <li>The edge is removed from the management VPN (no SSH over the VPN).</li>
            <li>
              It cannot renew its certificate or enroll again; open install tokens stop working.
            </li>
            <li>Data already received stays. The edge keeps buffering locally (7 days).</li>
          </ul>
          <p>Enable it again later to lift the ban; the VPN then needs a new key.</p>
        </ConfirmDialog>
      )}
      {confirm === 'enable' && (
        <ConfirmDialog
          title={`Enable ${edge.edge_node_id}?`}
          confirmLabel="Enable edge"
          busy={busy === 'enable'}
          error={error}
          onConfirm={() => {
            void toggle('enable');
          }}
          onCancel={() => {
            setConfirm(null);
          }}
        >
          <p>
            The MQTT ban is lifted: the edge reconnects with its certificate and sends its buffered
            data. If the certificate expired while disabled, re-enroll it. Afterwards get a new VPN
            key to bring it back into the VPN.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}
